import { useEffect, useState } from "react";
import { Download, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { eur } from "@/lib/format";
import { downloadFile } from "@/lib/airep";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const yBtn = "bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2 rounded-lg hover:brightness-95 inline-flex items-center gap-2 text-sm";

const Card = ({ label, value, testid }) => (
  <div className="bg-card rounded-xl border border-border/80 p-4" data-testid={testid}>
    <div className="text-xs text-muted-foreground">{label}</div><div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

function ServiceTable({ d }) {
  const rows = Object.entries(d.services).sort((a, b) => b[1].gross - a[1].gross);
  return (
    <div className="bg-card rounded-xl border border-border/80 p-5 overflow-x-auto" data-testid="revenue-services">
      <h2 className="font-semibold mb-3">Incassi per servizio ({d.year})</h2>
      {!rows.length ? <p className="text-sm text-muted-foreground">Nessun incasso nell'anno.</p> : (
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Servizio</th><th>Pagamenti</th><th>Imponibile</th><th>IVA</th><th>Totale</th><th>Comm. PayPal</th><th>Netto</th></tr></thead>
          <tbody>{rows.map(([k, s]) => (
            <tr key={k} className="border-t border-border" data-testid={`revenue-service-${k}`}><td className="py-2 font-medium">{d.labels[k] || k}</td><td>{s.count}</td><td>{eur(s.net)}</td><td>{eur(s.vat)}</td><td>{eur(s.gross)}</td><td className="text-rose-600">-{eur(s.fee)}</td><td className="font-semibold">{eur(s.netto)}</td></tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function MonthTable({ d, onExport }) {
  const svc = Object.keys(d.services);
  return (
    <div className="bg-card rounded-xl border border-border/80 p-5 overflow-x-auto" data-testid="revenue-months">
      <h2 className="font-semibold mb-3">Mese per mese (totale incassato IVA inclusa)</h2>
      <table className="w-full text-sm min-w-[640px]">
        <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Mese</th>{svc.map((k) => <th key={k}>{d.labels[k] || k}</th>)}<th>Totale</th><th>Netto</th><th /></tr></thead>
        <tbody>{MONTHS.map((label, i) => {
          const mm = String(i + 1).padStart(2, "0");
          const row = d.months[`${d.year}-${mm}`] || {};
          const tot = Object.values(row).reduce((a, s) => ({ gross: a.gross + s.gross, netto: a.netto + s.netto }), { gross: 0, netto: 0 });
          return (
            <tr key={mm} className="border-t border-border" data-testid={`revenue-month-${mm}`}>
              <td className="py-2 font-medium">{label}</td>{svc.map((k) => <td key={k}>{row[k] ? eur(row[k].gross) : "—"}</td>)}
              <td className="font-semibold">{tot.gross ? eur(tot.gross) : "—"}</td><td>{tot.netto ? eur(tot.netto) : "—"}</td>
              <td>{tot.gross ? <button onClick={() => onExport(mm)} className="text-xs underline" data-testid={`revenue-export-${mm}`}>CSV</button> : null}</td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

const COLORS = ["#111111", "#E3FF00", "#0ea5e9", "#16a34a", "#f97316", "#7c3aed", "#db2777", "#64748b"];

function RevenueChart({ d }) {
  const [svc, setSvc] = useState("all");
  const keys = svc === "all" ? Object.keys(d.services) : [svc];
  const data = MONTHS.map((m, i) => {
    const mm = String(i + 1).padStart(2, "0");
    const cur = d.months[`${d.year}-${mm}`] || {};
    const pv = d.prev_months[mm] || {};
    const row = { m, prev: (svc === "all" ? Object.values(pv).reduce((a, v) => a + v, 0) : pv[svc] || 0) / 100 };
    keys.forEach((k) => { row[k] = (cur[k]?.gross || 0) / 100; });
    return row;
  });
  const all = new Set([...Object.keys(d.services), ...Object.values(d.prev_months).flatMap(Object.keys)]);
  return (
    <div className="bg-card rounded-xl border border-border/80 p-5" data-testid="revenue-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h2 className="font-semibold">Andamento mensile {d.year} vs {Number(d.year) - 1}</h2>
        <select value={svc} onChange={(e) => setSvc(e.target.value)} className="bg-background border border-input rounded-lg px-3 py-2 text-sm" data-testid="revenue-chart-service">
          <option value="all">Tutti i servizi</option>
          {[...all].map((k) => <option key={k} value={k}>{d.labels[k] || k}</option>)}
        </select>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ left: -10, right: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="m" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}€`} />
            <Tooltip formatter={(v, n) => [`${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`, n]} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: "#111" }}>{v}</span>} />
            {keys.map((k, i) => <Bar key={k} dataKey={k} name={d.labels[k] || k} stackId="cur" fill={COLORS[i % COLORS.length]} stroke="#111" strokeWidth={0.5} radius={i === keys.length - 1 ? [4, 4, 0, 0] : 0} />)}
            <Line type="linear" dataKey="prev" name={`${svc === "all" ? "Totale" : d.labels[svc] || svc} ${Number(d.year) - 1}`} stroke="#64748b" strokeDasharray="5 4" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const EVENT_LABELS = { "PAYMENT.CAPTURE.REFUNDED": "Rimborso", "PAYMENT.CAPTURE.REVERSED": "Storno", "CUSTOMER.DISPUTE.CREATED": "Contestazione aperta", "CUSTOMER.DISPUTE.UPDATED": "Contestazione aggiornata", "CUSTOMER.DISPUTE.RESOLVED": "Contestazione chiusa" };

function EventsBox({ d }) {
  return (
    <div className="bg-card rounded-xl border border-border/80 p-5" data-testid="revenue-events">
      <h2 className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Rimborsi e contestazioni PayPal</h2>
      {!d.webhook_configured && <p className="text-xs text-amber-700 mt-1" data-testid="revenue-webhook-missing">Webhook PayPal non ancora collegato: le notifiche arriveranno dopo la configurazione.</p>}
      {!d.events.length ? <p className="text-sm text-muted-foreground mt-2">Nessun rimborso o contestazione.</p> : d.events.map((e) => (
        <div key={e.id} className="text-sm py-2 border-t border-border first:border-0 flex flex-wrap gap-x-3"><b>{EVENT_LABELS[e.type] || e.type}</b><span>{e.amount ? `${e.amount} €` : ""}</span><span className="text-muted-foreground">{e.reason || ""} {e.status || ""}</span><span className="text-muted-foreground">{new Date(e.received_at).toLocaleString("it-IT")}</span></div>
      ))}
    </div>
  );
}

export function RevenueTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/revenue", { params: { year } }).then(({ data }) => setD(data)); }, [year]);
  if (!d) return null;
  const exp = (m) => downloadFile(`/admin/revenue/export?year=${d.year}${m ? `&month=${m}` : ""}`, `incassi-${d.year}${m ? `-${m}` : ""}.csv`);
  const usage = Object.entries(d.wallet_usage);
  return (
    <div className="space-y-6" data-testid="admin-revenue-tab">
      <div className="flex flex-wrap items-center gap-3">
        <select value={d.year} onChange={(e) => setYear(e.target.value)} className="bg-background border border-input rounded-lg px-3 py-2 text-sm" data-testid="revenue-year">
          {d.years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={() => exp()} className={yBtn} data-testid="revenue-export-year"><Download className="h-4 w-4" /> Esporta anno (CSV per Excel)</button>
        <span className="text-xs text-muted-foreground">Pagamenti incassati con PayPal (carta o conto). Il file si apre direttamente in Excel.</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card label="Totale incassato" value={eur(d.total.gross)} testid="revenue-total-gross" />
        <Card label="di cui IVA" value={eur(d.total.vat)} testid="revenue-total-vat" />
        <Card label="Commissioni PayPal" value={d.total.fee ? `-${eur(d.total.fee)}` : eur(0)} testid="revenue-total-fee" />
        <Card label="Netto (dopo commissioni)" value={eur(d.total.netto)} testid="revenue-total-netto" />
      </div>
      <RevenueChart d={d} />
      <ServiceTable d={d} />
      <MonthTable d={d} onExport={exp} />
      <EventsBox d={d} />
      <div className="bg-card rounded-xl border border-border/80 p-5" data-testid="revenue-wallet-usage">
        <h2 className="font-semibold">Consumi dal borsellino ({d.year})</h2>
        <p className="text-xs text-muted-foreground mb-3">Credito già incassato con le ricariche e poi speso dai clienti nei servizi (non si somma agli incassi).</p>
        {!usage.length ? <p className="text-sm text-muted-foreground">Nessun consumo.</p> : usage.map(([k, u]) => (
          <div key={k} className="flex justify-between text-sm py-1.5 border-t border-border first:border-0" data-testid={`revenue-usage-${k}`}><span>{d.usage_labels[k] || k} · {u.count}</span><b>{eur(u.amount)}</b></div>
        ))}
      </div>
    </div>
  );
}
